"use client";

import {
  Building2,
  Calendar,
  CheckCircle,
  Eye,
  FileText,
  Pencil,
  Shield,
} from "lucide-react";

import { Button } from "@/components/forms/Button";
import { Typography } from "@/components/common/ui/Typography";
import { cn } from "@/lib/utils/cn";
import { formatDateShort } from "@/lib/utils";

export interface LossRunClaim {
  id: number;
  claim_id: string;
  loss_year: number | null;
  description: string;
  loss_incurred: number;
  recovery: number | null;
  carrier: string;
  coverage_type: string;
  source_attachment_id?: number | null;
}

export interface LossRunPeriod<TClaim extends LossRunClaim = LossRunClaim> {
  source_attachment_id: number | null;
  generation_date: string | null;
  total_loss_incurred: number;
  policy_number: string | null;
  named_insured: string | null;
  insurer: string | null;
  coverage_start_date: string | null;
  coverage_end_date: string | null;
  document_date: string | null;
  claims: TClaim[];
}

/**
 * Resolves one field of a period or claim to a click that highlights it in the
 * source PDF, or `undefined` when there are no coordinates for it (TP-496).
 *
 * Optional, and absent it the card renders exactly as it always has — multi-rater
 * passes neither and is unchanged. The card deliberately does not know how the
 * lookup works: the caller owns the provenance shape and the highlight store.
 */
type FieldAnchor = (field: string) => (() => void) | undefined;

interface LossRunPeriodCardProps<TClaim extends LossRunClaim> {
  lossRun: LossRunPeriod<TClaim>;
  onViewAttachment?: (attachmentId: number) => void;
  onEditPeriod?: () => void;
  onEditClaim?: (claim: TClaim) => void;
  onViewClaimSource?: (claim: TClaim) => void;
  /** Anchors the period header's own fields (policy_number, insurer, dates). */
  anchorPeriodField?: FieldAnchor;
  /**
   * Anchors one claim's fields. Takes the claim because each row resolves
   * against ITS OWN provenance: after cross-document dedup two rows in one period
   * can come from different PDFs, so a single anchor for the table would send
   * every row to whichever document the first one came from.
   */
  anchorClaimField?: (claim: TClaim, field: string) => (() => void) | undefined;
}

/**
 * Wraps a value in the click affordance when there is somewhere to click to.
 *
 * With no handler it renders its children and nothing else — same markup, same
 * spacing. That is the point: a field whose coordinates the matcher missed must
 * read as ordinary text, not as a button that ignores you.
 */
function Anchored({
  onClick,
  label,
  block = false,
  children,
}: {
  onClick?: () => void;
  label: string;
  /**
   * Render as a block rather than inline. Required around any child that
   * truncates.
   *
   * `truncate` sets `overflow:hidden`, which does nothing on an inline box — so a
   * long value inside an inline wrapper keeps only `white-space:nowrap` and spills
   * into the next grid column instead of clipping. The values in these cells used
   * to be direct flex items, which CSS blockifies for free; wrapping them for the
   * click affordance is what took that away. Bar & Tavern hit the identical trap
   * from the other direction in BAR-273.
   */
  block?: boolean;
  children: React.ReactNode;
}) {
  if (!onClick) return <>{children}</>;
  return (
    <span
      role="button"
      tabIndex={0}
      title={`View ${label} in the source document`}
      onClick={onClick}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onClick();
        }
      }}
      className={cn(
        "-mx-1 cursor-pointer rounded px-1 hover:bg-blue-50",
        // `min-w-0` with it: a block flex item still refuses to shrink below its
        // content without it, so the child would clip at a width that never
        // narrows and the overflow would come straight back.
        block && "block min-w-0",
      )}
    >
      {children}
    </span>
  );
}

function fmtCurrency(value: number | null | undefined): string {
  const amount = value ?? 0;
  return `$${amount.toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
}

function ClaimsTable<TClaim extends LossRunClaim>({
  claims,
  onEditClaim,
  onViewClaimSource,
  anchorClaimField,
}: {
  claims: TClaim[];
  onEditClaim?: (claim: TClaim) => void;
  onViewClaimSource?: (claim: TClaim) => void;
  anchorClaimField?: (claim: TClaim, field: string) => (() => void) | undefined;
}) {
  const hasActions = onEditClaim || onViewClaimSource;
  return (
    <div className="overflow-x-auto">
      <table className="w-full">
        <thead>
          <tr className="bg-gray-50">
            <Typography as="th" variant="overline" color="light" className="text-left py-2 px-3">Claim ID</Typography>
            <Typography as="th" variant="overline" color="light" className="text-left py-2 px-3">Year</Typography>
            <Typography as="th" variant="overline" color="light" className="text-left py-2 px-3">Coverage</Typography>
            <Typography as="th" variant="overline" color="light" className="text-left py-2 px-3">Carrier</Typography>
            <Typography as="th" variant="overline" color="light" className="text-left py-2 px-3">Description</Typography>
            <Typography as="th" variant="overline" color="light" className="text-right py-2 px-3">Loss Incurred</Typography>
            <Typography as="th" variant="overline" color="light" className="text-right py-2 px-3">Recovery</Typography>
            <Typography as="th" variant="overline" color="light" className="text-right py-2 px-3">Net</Typography>
            {hasActions && <th className="w-20" aria-label="Actions" />}
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-100">
          {claims.map((claim) => (
            <tr key={claim.id} className="hover:bg-gray-50 transition-colors">
              <td className="py-2 px-3">
                <Anchored onClick={anchorClaimField?.(claim, "claim_id")} label="claim ID">
                  <Typography as="span" variant="body" mono color="text-gray-700">
                    {claim.claim_id || <span className="text-gray-400 italic">N/A</span>}
                  </Typography>
                </Anchored>
              </td>
              <td className="py-2 px-3">
                {/* Never anchored. A bare four-digit year appears all over a
                    claims table, so the matcher pins it to whichever row it saw
                    first — the coordinates exist and point at another claim. */}
                <Typography as="span" variant="body" color="text-gray-700">
                  {claim.loss_year ?? <span className="text-gray-400 italic">N/A</span>}
                </Typography>
              </td>
              <td className="py-2 px-3">
                <Typography as="span" variant="body" color="text-gray-700">
                  {claim.coverage_type || <span className="text-gray-400 italic">N/A</span>}
                </Typography>
              </td>
              <td className="py-2 px-3">
                <Anchored onClick={anchorClaimField?.(claim, "carrier")} label="carrier">
                  <Typography as="span" variant="body" color="text-gray-700">
                    {claim.carrier || <span className="text-gray-400 italic">N/A</span>}
                  </Typography>
                </Anchored>
              </td>
              <td className="py-2 px-3 max-w-xs">
                <Anchored onClick={anchorClaimField?.(claim, "description")} label="description">
                  <Typography as="span" variant="body" color="text-gray-700" className="whitespace-normal break-words">
                    {claim.description || <span className="text-gray-400 italic">N/A</span>}
                  </Typography>
                </Anchored>
              </td>
              <td className="py-2 px-3 text-right">
                <Anchored
                  onClick={anchorClaimField?.(claim, "loss_incurred")}
                  label="loss incurred"
                >
                  <Typography
                    as="span"
                    variant="body"
                    mono
                    weight="semibold"
                    color={claim.loss_incurred > 0 ? "text-red-600" : "text-green-600"}
                  >
                    {fmtCurrency(claim.loss_incurred)}
                  </Typography>
                </Anchored>
              </td>
              <td className="py-2 px-3 text-right">
                {claim.recovery != null && claim.recovery > 0 ? (
                  <Typography as="span" variant="body" mono color="text-gray-700">
                    {fmtCurrency(claim.recovery)}
                  </Typography>
                ) : (
                  <Typography as="span" variant="body" color="light">
                    —
                  </Typography>
                )}
              </td>
              <td className="py-2 px-3 text-right">
                {(() => {
                  const net = claim.loss_incurred - (claim.recovery ?? 0);
                  return (
                    <Typography
                      as="span"
                      variant="body"
                      mono
                      weight="semibold"
                      color={net > 0 ? "text-red-600" : "text-green-600"}
                    >
                      {fmtCurrency(net)}
                    </Typography>
                  );
                })()}
              </td>
              {hasActions && (
                <td className="py-1 px-2 text-right whitespace-nowrap">
                  {onViewClaimSource && claim.source_attachment_id != null && (
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      icon={Eye}
                      iconOnly
                      aria-label={`View source for claim ${claim.claim_id || claim.id}`}
                      onClick={() => onViewClaimSource(claim)}
                    />
                  )}
                  {onEditClaim && (
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      icon={Pencil}
                      iconOnly
                      aria-label={`Edit claim ${claim.claim_id || claim.id}`}
                      onClick={() => onEditClaim(claim)}
                    />
                  )}
                </td>
              )}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export function LossRunPeriodCard<TClaim extends LossRunClaim>({
  lossRun,
  onViewAttachment,
  onEditPeriod,
  onEditClaim,
  onViewClaimSource,
  anchorPeriodField,
  anchorClaimField,
}: LossRunPeriodCardProps<TClaim>) {
  const hasNoClaims = lossRun.claims.length === 0;
  const canView = onViewAttachment && lossRun.source_attachment_id != null;
  const periodRecovery = lossRun.claims.reduce(
    (sum, c) => sum + (c.recovery ?? 0),
    0,
  );
  const periodNet = lossRun.total_loss_incurred - periodRecovery;

  return (
    <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
      {/* Policy header */}
      <div className="px-4 py-3 border-b border-gray-100 flex items-start justify-between gap-4 flex-wrap">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1.5 mb-0.5">
            <Shield className="w-3.5 h-3.5 text-blue-500 opacity-70" />
            <Typography as="span" variant="caption" color="light" className="uppercase tracking-wide">Policy</Typography>
          </div>
          <Anchored onClick={anchorPeriodField?.("policy_number")} label="policy number">
            <Typography as="h4" variant="body" weight="semibold" color="text-gray-800" truncate>
              {lossRun.policy_number || "Unknown Policy"}
            </Typography>
          </Anchored>
        </div>
        <div className="flex items-center gap-2">
          {(lossRun.document_date || lossRun.generation_date) && (
            <span className="inline-flex items-center gap-1 bg-gray-100 rounded-full px-2.5 py-1">
              <Calendar className="w-3 h-3" />
              <Typography as="span" variant="small" color="light">
                {formatDateShort(lossRun.document_date || lossRun.generation_date)}
              </Typography>
            </span>
          )}
          {canView && (
            <Button
              size="sm"
              variant="ghost"
              startIcon={<Eye className="h-3.5 w-3.5" />}
              onClick={() =>
                onViewAttachment!(lossRun.source_attachment_id as number)
              }
              type="button"
            >
              View in viewer
            </Button>
          )}
          {onEditPeriod && (
            <Button
              type="button"
              variant="white"
              size="sm"
              startIcon={<Pencil className="h-3.5 w-3.5" />}
              onClick={onEditPeriod}
            >
              Edit
            </Button>
          )}
        </div>
      </div>

      {/* Policy metadata */}
      <div className="px-4 py-3 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
        <div className="flex flex-col gap-0.5">
          <Typography as="span" variant="caption" color="light" className="uppercase">Named Insured</Typography>
          <Typography as="span" variant="body" weight="medium" color="text-gray-700" truncate>
            {lossRun.named_insured || <span className="text-gray-400 italic">N/A</span>}
          </Typography>
        </div>
        <div className="flex flex-col gap-0.5">
          <Typography as="span" variant="caption" color="light" className="uppercase flex items-center gap-1">
            <Building2 className="w-3 h-3" />
            Insurer
          </Typography>
          <Anchored onClick={anchorPeriodField?.("insurer")} label="insurer" block>
            <Typography as="span" variant="body" weight="medium" color="text-gray-700" truncate className="block">
              {lossRun.insurer || <span className="text-gray-400 italic">N/A</span>}
            </Typography>
          </Anchored>
        </div>
        <div className="flex flex-col gap-0.5">
          <Typography as="span" variant="caption" color="light" className="uppercase">Coverage Period</Typography>
          {/* One anchor for the pair, on the start date. The two dates render as a
              single range, and dedup can take them from different documents — so
              splitting the cell in two would let one click jump to another
              carrier's PDF while the other stayed put. The start date is the one an
              underwriter checks a gap against. */}
          <Anchored
            onClick={anchorPeriodField?.("coverage_start_date")}
            label="coverage period"
          >
            <Typography as="span" variant="body" weight="medium" color="text-gray-700">
              {lossRun.coverage_start_date || lossRun.coverage_end_date ? (
                <>
                  {formatDateShort(lossRun.coverage_start_date)} —{" "}
                  {formatDateShort(lossRun.coverage_end_date)}
                </>
              ) : (
                <span className="text-gray-400 italic">N/A</span>
              )}
            </Typography>
          </Anchored>
        </div>
        <div className="flex flex-col gap-0.5">
          <Typography as="span" variant="caption" color="light" className="uppercase">Loss Incurred</Typography>
          <Typography as="span" variant="body" weight="medium" mono color="text-gray-700">
            {fmtCurrency(lossRun.total_loss_incurred)}
          </Typography>
        </div>
        <div className="flex flex-col gap-0.5">
          <Typography as="span" variant="caption" color="light" className="uppercase">Recovery</Typography>
          <Typography as="span" variant="body" weight="medium" mono color="text-gray-700">
            {periodRecovery > 0 ? (
              fmtCurrency(periodRecovery)
            ) : (
              <span className="text-gray-400 italic">—</span>
            )}
          </Typography>
        </div>
        <div className="flex flex-col gap-0.5">
          <Typography as="span" variant="caption" color="light" className="uppercase">Net Loss</Typography>
          <Typography
            as="span"
            variant="body"
            weight="bold"
            mono
            color={periodNet > 0 ? "text-red-600" : "text-green-600"}
          >
            {fmtCurrency(periodNet)}
          </Typography>
        </div>
      </div>

      {hasNoClaims && (
        <div className="mx-4 mb-3 flex items-center gap-2 p-2.5 rounded-lg bg-green-50 border border-green-200">
          <CheckCircle className="w-4 h-4 text-green-600" />
          <Typography as="span" variant="body" weight="medium" color="text-green-700">
            No losses incurred
          </Typography>
        </div>
      )}

      {!hasNoClaims && (
        <div className="px-4 pb-3">
          <div className="mb-1.5 flex items-center gap-1">
            <FileText className="w-3 h-3 text-gray-400" />
            <Typography as="span" variant="small" color="light">
              {lossRun.claims.length} claim{lossRun.claims.length !== 1 ? "s" : ""}
            </Typography>
          </div>
          <ClaimsTable
            claims={lossRun.claims}
            onEditClaim={onEditClaim}
            onViewClaimSource={onViewClaimSource}
            anchorClaimField={anchorClaimField}
          />
        </div>
      )}
    </div>
  );
}
